import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { AuthContext, API } from "../App";
import { Button } from "../components/ui/button";
import {
  ArrowLeft, Send, Search, MessageCircle, Plus, X, User
} from "lucide-react";
import { toast } from "sonner";
import { devError } from "../utils/devLogger";

const Messages = () => {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [activeChat, setActiveChat] = useState(null); // partner user object
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchMode, setSearchMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  const fetchConversations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/messages/conversations`);
      setConversations(res.data.conversations);
    } catch (error) {
      devError("Failed to fetch conversations:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Poll for new messages when in active chat
  useEffect(() => {
    if (!activeChat) return;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/messages/${activeChat.partner_id}`);
        setMessages(res.data.messages);
      } catch (error) {
        devError("Poll error:", error);
      }
    };
    poll();
    pollRef.current = setInterval(poll, 5000);
    return () => clearInterval(pollRef.current);
  }, [activeChat]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    if (!newMsg.trim() || !activeChat || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/messages/send`, {
        recipient_id: activeChat.partner_id,
        text: newMsg.trim(),
      });
      setNewMsg("");
      // Refresh messages
      const res = await axios.get(`${API}/messages/${activeChat.partner_id}`);
      setMessages(res.data.messages);
      fetchConversations();
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleSearch = async (q) => {
    setSearchQuery(q);
    if (q.length < 2) { setSearchResults([]); return; }
    try {
      const res = await axios.get(`${API}/users/search?q=${encodeURIComponent(q)}`);
      setSearchResults(res.data.users);
    } catch (error) {
      devError("Search error:", error);
    }
  };

  const startConversation = (searchUser) => {
    setActiveChat({
      partner_id: searchUser.user_id,
      partner_name: searchUser.name,
      partner_avatar: searchUser.avatar,
    });
    setSearchMode(false);
    setSearchQuery("");
    setSearchResults([]);
  };

  const openConversation = (convo) => {
    setActiveChat({
      partner_id: convo.partner_id,
      partner_name: convo.partner_name,
      partner_avatar: convo.partner_avatar,
    });
  };

  const formatTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return "now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
    if (diff < 86400000) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-sky-50 flex items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-sky-400/30 glow-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sky-50" data-testid="messages-page">
      <div className="fixed top-0 left-0 w-64 h-64 bg-indigo-300/10 rounded-full blur-3xl pointer-events-none" />
      <div className="fixed bottom-0 right-0 w-80 h-80 bg-sky-300/10 rounded-full blur-3xl pointer-events-none" />

      <header className="fixed top-0 w-full z-50 glass-header">
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => activeChat ? (setActiveChat(null), clearInterval(pollRef.current)) : navigate("/dashboard")}
              className="text-sky-600 hover:text-sky-800"
              data-testid="messages-back-btn"
            >
              <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
              {activeChat ? activeChat.partner_name : t("common.dashboard")}
            </Button>
            <div className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-sky-500" strokeWidth={1.5} />
              <span className="text-sm text-sky-700">Messages</span>
            </div>
            {!activeChat && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSearchMode(!searchMode)}
                className="text-sky-600"
                data-testid="new-message-btn"
              >
                {searchMode ? <X className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="pt-20 pb-4 px-4 md:px-8">
        <div className="max-w-3xl mx-auto">

          {/* Search Mode */}
          {searchMode && !activeChat && (
            <div className="glass-card p-4 mb-4 animate-in fade-in duration-200" data-testid="user-search">
              <div className="flex items-center gap-2 mb-3">
                <Search className="w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Search users by name..."
                  className="flex-1 bg-transparent outline-none text-sm text-sky-900"
                  autoFocus
                  data-testid="user-search-input"
                />
              </div>
              {searchResults.length > 0 && (
                <div className="space-y-1">
                  {searchResults.map((u) => (
                    <button
                      key={u.user_id}
                      onClick={() => startConversation(u)}
                      className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-sky-50 transition-colors text-left"
                      data-testid={`search-result-${u.user_id}`}
                    >
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-400 to-indigo-400 flex items-center justify-center">
                        <span className="text-xs text-white font-medium">{u.name?.[0]}</span>
                      </div>
                      <span className="text-sm text-sky-800">{u.name}</span>
                    </button>
                  ))}
                </div>
              )}
              {searchQuery.length >= 2 && searchResults.length === 0 && (
                <p className="text-sm text-slate-400 text-center py-2">No users found</p>
              )}
            </div>
          )}

          {/* Conversation List */}
          {!activeChat && !searchMode && (
            <div data-testid="conversations-list">
              {conversations.length === 0 ? (
                <div className="text-center py-16">
                  <MessageCircle className="w-16 h-16 text-sky-200 mx-auto mb-4" strokeWidth={1} />
                  <h3 className="text-lg font-light text-sky-800 mb-2" style={{ fontFamily: "Outfit" }}>
                    No messages yet
                  </h3>
                  <p className="text-sm text-slate-400 mb-4">Start a conversation with another LumiFit user</p>
                  <Button
                    onClick={() => setSearchMode(true)}
                    className="glow-button"
                    data-testid="start-conversation-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" /> New Message
                  </Button>
                </div>
              ) : (
                <div className="space-y-1">
                  {conversations.map((convo) => (
                    <button
                      key={convo.partner_id}
                      onClick={() => openConversation(convo)}
                      className="w-full flex items-center gap-3 p-4 rounded-xl bg-white/60 hover:bg-white/90 border border-transparent hover:border-sky-100 transition-all text-left"
                      data-testid={`convo-${convo.partner_id}`}
                    >
                      <div className="relative">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-sky-400 to-indigo-400 flex items-center justify-center">
                          <span className="text-sm text-white font-medium">{convo.partner_name?.[0]}</span>
                        </div>
                        {convo.unread_count > 0 && (
                          <div className="absolute -top-1 -right-1 w-5 h-5 bg-sky-500 rounded-full flex items-center justify-center">
                            <span className="text-[10px] text-white font-bold">{convo.unread_count}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-sky-900">{convo.partner_name}</p>
                          <span className="text-xs text-slate-400">{formatTime(convo.last_at)}</span>
                        </div>
                        <p className="text-xs text-slate-500 truncate mt-0.5">{convo.last_message}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Active Chat */}
          {activeChat && (
            <div className="flex flex-col" style={{ height: "calc(100vh - 140px)" }} data-testid="active-chat">
              {/* Messages */}
              <div className="flex-1 overflow-y-auto space-y-3 pb-4 pr-1">
                {messages.length === 0 && (
                  <div className="text-center py-12">
                    <p className="text-sm text-slate-400">Send the first message!</p>
                  </div>
                )}
                {messages.map((msg, i) => {
                  const isMe = msg.sender_id === user?.user_id;
                  return (
                    <div
                      key={msg.id || `msg-${i}`}
                      className={`flex ${isMe ? "justify-end" : "justify-start"}`}
                      data-testid={`message-${i}`}
                    >
                      <div
                        className={`max-w-[75%] px-4 py-2.5 rounded-2xl ${
                          isMe
                            ? "bg-sky-500 text-white rounded-br-md"
                            : "bg-white/80 text-sky-900 border border-sky-100 rounded-bl-md"
                        }`}
                      >
                        <p className="text-sm leading-relaxed">{msg.text}</p>
                        <p className={`text-[10px] mt-1 ${isMe ? "text-sky-200" : "text-slate-400"}`}>
                          {formatTime(msg.created_at)}
                        </p>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="flex items-center gap-2 p-3 bg-white/70 backdrop-blur-sm rounded-2xl border border-sky-100">
                <input
                  type="text"
                  value={newMsg}
                  onChange={(e) => setNewMsg(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSend()}
                  placeholder="Type a message..."
                  className="flex-1 bg-transparent outline-none text-sm text-sky-900 placeholder:text-slate-400"
                  autoFocus
                  data-testid="message-input"
                />
                <Button
                  onClick={handleSend}
                  disabled={!newMsg.trim() || sending}
                  className="glow-button px-3 py-2"
                  data-testid="send-message-btn"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Messages;
